import React from "react"

import { Button, Tooltip } from "@coval/ui"
import { Play, RotateCw } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"

import { useTestMetricRuntime } from "../contexts"
import { useCanRunEvaluations } from "../hooks/useCanRunEvaluations"
import { formatTraceValues } from "../recentMetricValues"
import type { ExperimentTestResult as TestResult } from "../types"
import { CompositeReviewMatrix } from "./CompositeReviewMatrix"
import { LoadingIndicator } from "./LoadingIndicator"
import { ReviewBadge } from "./ReviewBadge"
import { TranscriptDetailView } from "./TranscriptDetailView"

interface RecentDetailViewProps {
  testResult: TestResult | null
  isEvaluating: boolean
  currentIndex: number
  totalCount: number
  onNext: () => void
  onPrevious: () => void
  onRunTest: () => void
}

export const RecentDetailView: React.FC<RecentDetailViewProps> = ({
  testResult,
  isEvaluating,
  currentIndex,
  totalCount,
  onNext,
  onPrevious,
  onRunTest,
}) => {
  const { metric } = useTestMetricRuntime()
  const { trackEvent } = useAnalytics()
  const canRunEvaluations = useCanRunEvaluations()
  const isCompositeMetric =
    metric?.metric_manager === "manager_composite_evaluation"
  const isTraceValues =
    metric?.metric_manager === "manager_custom_trace" &&
    (testResult?.rawMetricOutput?.output_type === "set" ||
      metric?.metric_metadata?.aggregation_method === "values")
  const testTraceValues =
    isTraceValues &&
    (!testResult?.errorStatus || testResult.errorStatus === "COMPLETED") &&
    testResult?.rawMetricOutput?.output_type === "set" &&
    Array.isArray(testResult.rawMetricOutput.set_value)
      ? testResult.rawMetricOutput.set_value
      : null

  const handleRunTest = () => {
    trackEvent(TRACKING_EVENTS.METRICS.TEST_METRIC_BUTTON_CLICKED, {
      metric_id: metric?.id,
    })
    onRunTest()
  }

  const renderReviewSection = () => (
    <div
      className="p-4 border-b border-coval-border shrink-0"
      data-dd-privacy={isTraceValues ? "mask" : undefined}
      data-ph-capture={isTraceValues ? "false" : undefined}
    >
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-coval-text">Test Result</h3>
        {isEvaluating ? (
          <div className="flex items-center gap-2">
            <LoadingIndicator />
            <span className="text-xs text-coval-text-muted">Evaluating...</span>
          </div>
        ) : testResult ? (
          <>
            <div className="flex items-center gap-2">
              {testTraceValues ? (
                <span className="text-sm text-coval-text">
                  {formatTraceValues(testTraceValues)}
                </span>
              ) : (
                <ReviewBadge
                  value={testResult.value}
                  targetCondition={metric?.metric_metadata?.target_condition}
                  errorStatus={testResult.errorStatus}
                  errorMessage={testResult.errorMessage}
                />
              )}
              {canRunEvaluations && (
                <Tooltip content="Rerun test" triggerDisabled={isEvaluating}>
                  <button
                    aria-label="Rerun test"
                    onClick={handleRunTest}
                    disabled={isEvaluating}
                    className="text-coval-text-muted hover:text-coval-text transition-colors disabled:opacity-50"
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                  </button>
                </Tooltip>
              )}
            </div>
            {isCompositeMetric &&
            testResult.criteriaResults &&
            testResult.criteriaResults.length > 0 ? (
              <CompositeReviewMatrix
                criteria={testResult.criteriaResults.map((c) => c.criterion)}
                reviewers={[
                  {
                    key: "test",
                    label: "Test Result",
                    results: testResult.criteriaResults,
                  },
                ]}
              />
            ) : (
              testResult.explanation && (
                <p className="text-xs text-coval-text">
                  {testResult.explanation}
                </p>
              )
            )}
          </>
        ) : canRunEvaluations ? (
          <>
            <p className="text-xs text-coval-text-muted">
              Run a test to see results
            </p>
            <Button
              variant="secondary"
              onClick={handleRunTest}
              disabled={!metric?.metric_metadata?.question || isEvaluating}
              aria-label="Run test"
            >
              <Play className="w-3.5 h-3.5" />
              Run Test
            </Button>
          </>
        ) : null}
      </div>
    </div>
  )

  return (
    <TranscriptDetailView
      currentIndex={currentIndex}
      totalCount={totalCount}
      onNext={onNext}
      onPrevious={onPrevious}
      renderReviewSection={renderReviewSection}
    />
  )
}
